var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __generator = (this && this.__generator) || function (thisArg, body) {
    var _ = { label: 0, sent: function() { if (t[0] & 1) throw t[1]; return t[1]; }, trys: [], ops: [] }, f, y, t, g;
    return g = { next: verb(0), "throw": verb(1), "return": verb(2) }, typeof Symbol === "function" && (g[Symbol.iterator] = function() { return this; }), g;
    function verb(n) { return function (v) { return step([n, v]); }; }
    function step(op) {
        if (f) throw new TypeError("Generator is already executing.");
        while (g && (g = 0, op[0] && (_ = 0)), _) try {
            if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
            if (y = 0, t) op = [op[0] & 2, t.value];
            switch (op[0]) {
                case 0: case 1: t = op; break;
                case 4: _.label++; return { value: op[1], done: false };
                case 5: _.label++; y = op[1]; op = [0]; continue;
                case 7: op = _.ops.pop(); _.trys.pop(); continue;
                default:
                    if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) { _ = 0; continue; }
                    if (op[0] === 3 && (!t || (op[1] > t[0] && op[1] < t[3]))) { _.label = op[1]; break; }
                    if (op[0] === 6 && _.label < t[1]) { _.label = t[1]; t = op; break; }
                    if (t && _.label < t[2]) { _.label = t[2]; _.ops.push(op); break; }
                    if (t[2]) _.ops.pop();
                    _.trys.pop(); continue;
            }
            op = body.call(thisArg, _);
        } catch (e) { op = [6, e]; y = 0; } finally { f = t = 0; }
        if (op[0] & 5) throw op[1]; return { value: op[0] ? op[1] : void 0, done: true };
    }
};
import { useAuth0 } from "@auth0/auth0-react";
import { useEffect, useState } from "react";
import { useRequest } from "./useRequest";
var CACHE_KEY = "dw_admin_emails_v1";
/**
 * Returns whether the currently logged-in Auth0 user is in the backend admin email list.
 * The list is fetched once per tab session and cached in sessionStorage.
 *
 * IMPORTANT: callers that gate UI on `isAdmin` must first wait for `loading === false`.
 * `loading` stays true until (a) Auth0 is done resolving AND (b) the admin email list
 * has been fetched (or loaded from cache). Without this, a newly-mounted component
 * would see `isAdmin=false` on the first render and redirect real admins away.
 */
export var useAdminAccess = function () {
    var _a = useAuth0(), isAuthenticated = _a.isAuthenticated, isAuth0Loading = _a.isLoading, user = _a.user;
    var request = useRequest();
    var _b = useState({ emails: [], resolved: false }), state = _b[0], setState = _b[1];
    useEffect(function () {
        // Wait for Auth0 to finish hydrating before deciding anything. Running this effect
        // while Auth0 is still loading would briefly see `isAuthenticated=false` and
        // incorrectly mark `resolved=true` with an empty email list, which would redirect
        // real admins away.
        if (isAuth0Loading)
            return;
        var cancelled = false;
        var load = function () { return __awaiter(void 0, void 0, void 0, function () {
            var cached, parsed, response, emails, err_1;
            var _a;
            return __generator(this, function (_b) {
                switch (_b.label) {
                    case 0:
                        if (!isAuthenticated) {
                            if (!cancelled)
                                setState({ emails: [], resolved: true });
                            return [2 /*return*/];
                        }
                        // 1) Try sessionStorage cache first (synchronous, avoids roundtrip on refresh).
                        try {
                            cached = sessionStorage.getItem(CACHE_KEY);
                            if (cached) {
                                parsed = JSON.parse(cached);
                                if (Array.isArray(parsed)) {
                                    if (!cancelled)
                                        setState({ emails: parsed, resolved: true });
                                    return [2 /*return*/];
                                }
                            }
                        }
                        catch (_c) {
                            /* ignore bad cache */
                        }
                        _b.label = 1;
                    case 1:
                        _b.trys.push([1, 3, , 4]);
                        return [4 /*yield*/, request.get("/AdminCostDashboard/admin-emails")];
                    case 2:
                        response = _b.sent();
                        emails = ((_a = response === null || response === void 0 ? void 0 : response.data) === null || _a === void 0 ? void 0 : _a.emails) || [];
                        if (!cancelled)
                            setState({ emails: emails, resolved: true });
                        try {
                            sessionStorage.setItem(CACHE_KEY, JSON.stringify(emails));
                        }
                        catch (_d) {
                            /* ignore quota errors */
                        }
                        return [3 /*break*/, 4];
                    case 3:
                        err_1 = _b.sent();
                        console.warn("Failed to fetch admin emails", err_1);
                        if (!cancelled)
                            setState({ emails: [], resolved: true });
                        return [3 /*break*/, 4];
                    case 4: return [2 /*return*/];
                }
            });
        }); };
        load();
        return function () {
            cancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isAuth0Loading, isAuthenticated]);
    var email = ((user === null || user === void 0 ? void 0 : user.email) || "").toLowerCase().trim();
    var isAdmin = !!email && state.emails.some(function (e) { return e.toLowerCase().trim() === email; });
    // We're loading until Auth0 has finished AND our email-list probe has settled.
    // Without this, a real admin sees `isAdmin=false` on the first render before the
    // list resolves, and any caller that redirects on `!isAdmin` would kick them out.
    var loading = isAuth0Loading || !state.resolved;
    return { isAdmin: isAdmin, loading: loading, adminEmails: state.emails };
};
